import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  clearAuth,
  getStoredToken,
  getStoredUsername,
  getStoredEmailConfirmPending,
  setStoredEmailConfirmPending,
} from '../services/authService';

export interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  /** True after register until the user confirms their email address */
  emailConfirmPending: boolean;
  isLoading: boolean;
  error: string | null;
  /**
   * True when the user was logged out because the refresh token itself
   * failed (i.e. it's expired/invalid), as opposed to a deliberate sign-out.
   * The UI uses this to automatically pop the login screen back up.
   */
  sessionExpired: boolean;
}

const initialState: AuthState = {
  isAuthenticated: !!getStoredToken(),
  username: getStoredUsername(),
  emailConfirmPending: getStoredEmailConfirmPending(),
  isLoading: false,
  error: null,
  sessionExpired: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(
      state,
      action: PayloadAction<{ username: string; emailConfirmPending?: boolean }>,
    ) {
      state.isAuthenticated = true;
      state.username = action.payload.username;
      // If the caller doesn't pass an explicit value, preserve whatever was
      // previously stored (e.g. a still-unconfirmed account signing back in).
      if (action.payload.emailConfirmPending !== undefined) {
        state.emailConfirmPending = action.payload.emailConfirmPending;
        setStoredEmailConfirmPending(action.payload.emailConfirmPending);
      }
      state.isLoading = false;
      state.error = null;
    },
    logout(state) {
      clearAuth();
      state.isAuthenticated = false;
      state.username = null;
      state.emailConfirmPending = false;
      state.error = null;
    },
    /** Refresh token turned out to be expired/invalid — force back to the login screen. */
    sessionExpired(state) {
      clearAuth();
      state.isAuthenticated = false;
      state.username = null;
      state.emailConfirmPending = false;
      state.error = null;
      state.sessionExpired = true;
    },
    /** Called once the UI has reacted to sessionExpired (shown the login modal). */
    acknowledgeSessionExpired(state) {
      state.sessionExpired = false;
    },
    emailConfirmed(state) {
      state.emailConfirmPending = false;
      setStoredEmailConfirmPending(false);
    },
    /** Called when a silent refresh succeeds mid-flight */
    tokenRefreshed(state) {
      state.isAuthenticated = true;
    },
    setAuthLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setAuthError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearAuthError(state) {
      state.error = null;
    },
  },
});

export const {
  loginSuccess,
  logout,
  sessionExpired,
  acknowledgeSessionExpired,
  emailConfirmed,
  tokenRefreshed,
  setAuthLoading,
  setAuthError,
  clearAuthError,
} = authSlice.actions;

export default authSlice.reducer;
